import * as React from 'react';
import { useAsyncResult } from 'react-use-async-result';

import { AppCtx } from '../context/appContext';
import type { FoodSearchIndex } from './food-search-index';
import {
  SEARCH_INDEX_DELTA_REFRESH_MS,
  SEARCH_INDEX_FULL_RELOAD_MS,
  SEARCH_INDEX_START_DELAY_MS,
  searchIndexRetryDelayMs,
} from './foodSearchIndexTiming';

export {
  SEARCH_INDEX_DELTA_REFRESH_MS,
  SEARCH_INDEX_FULL_RELOAD_MS,
  SEARCH_INDEX_START_DELAY_MS,
  searchIndexRetryDelayMs,
};

type FoodSearchIndexLoader = {
  events: EventTarget;
  init: () => unknown;
  refetch: () => unknown;
  shutdown: () => void;
};

const useFreshFoodSearch = () => {
  const indexRes = useAsyncResult<FoodSearchIndex>();
  const indexResRef = React.useRef(indexRes);
  indexResRef.current = indexRes;
  const [updateItemCount, setUpdateItemCount] = React.useState(0);
  const loaderRef = React.useRef<FoodSearchIndexLoader | null>(null);
  // Replaced on cleanup so an in-flight import from a discarded mount (including Strict Mode's
  // setup/cleanup/setup) cannot init a loader the new mount does not own.
  const sessionRef = React.useRef({ cancelled: false, startedFor: null as string | null });

  const { authInfo } = React.useContext(AppCtx);
  const accessToken = authInfo?.access_token ?? null;

  /** Starts loading the index once per sign-in; later calls do nothing. */
  const start = React.useCallback(() => {
    const session = sessionRef.current;
    if (!accessToken || session.cancelled || session.startedFor === accessToken) {
      return;
    }
    session.startedFor = accessToken;
    void import('./foodSearchIndexLoader').then(({ createFoodSearchIndexLoader }) => {
      if (sessionRef.current !== session || session.cancelled) {
        return;
      }
      if (!loaderRef.current) {
        const loader = createFoodSearchIndexLoader();
        loader.events.addEventListener('index-promise', event => {
          indexResRef.current.bind((event as CustomEvent).detail.promise);
        });
        loader.events.addEventListener('index-update', event => {
          setUpdateItemCount((event as CustomEvent).detail.newItemCount);
        });
        loaderRef.current = loader;
      }
      void loaderRef.current.init();
    });
  }, [accessToken]);

  React.useEffect(() => {
    if (!accessToken) {
      sessionRef.current = { cancelled: false, startedFor: null };
      return;
    }
    const session = sessionRef.current;
    session.cancelled = false;
    const timer = window.setTimeout(start, SEARCH_INDEX_START_DELAY_MS);
    const onInteract = () => start();
    window.addEventListener('pointerdown', onInteract, true);
    window.addEventListener('keydown', onInteract, true);
    return () => {
      session.cancelled = true;
      sessionRef.current = { cancelled: false, startedFor: null };
      window.clearTimeout(timer);
      window.removeEventListener('pointerdown', onInteract, true);
      window.removeEventListener('keydown', onInteract, true);
      loaderRef.current?.shutdown();
      loaderRef.current = null;
    };
  }, [accessToken, start]);

  (window as any).debugFoodSearchIndexRes = indexRes;
  (window as any).debugFoodSearchIndex = indexRes.isDone ? indexRes.result : null;

  const reload = () => {
    if (loaderRef.current) {
      loaderRef.current.refetch();
      return;
    }
    // A reload before the first download is just the first download.
    if (sessionRef.current.startedFor !== accessToken) {
      start();
    }
  };

  return {
    indexSize: indexRes.isDone ? indexRes.result.itemCount : updateItemCount,
    loadState: indexRes,
    ...indexRes,
    reload,
    start,
  };
};

export const FoodSearchCtx = React.createContext<ReturnType<typeof useFreshFoodSearch>>(null as any);

export const FoodSearchProvider = ({ children }: React.PropsWithChildren) => {
  const value = useFreshFoodSearch();
  return <FoodSearchCtx.Provider value={value}>{children}</FoodSearchCtx.Provider>;
};

/** For components that search: starts loading the index now if it has not started yet. */
export const useFoodSearchIndex = () => {
  const ctx = React.useContext(FoodSearchCtx);
  if (!ctx) {
    throw new Error('useFoodSearchIndex must be used within a FoodSearchProvider');
  }
  const { start } = ctx;
  React.useEffect(() => {
    start();
  }, [start]);
  return ctx;
};
